// src/components/WorkFlowsSuggestionLayer.jsx
import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect } from "react";
import LoadingComponent from "./ui/LoadingComponent";
import { apiCall } from "../Apis/apiCall";
import { useNavigate } from "react-router-dom";
import TableDataNoAvailable from "./ui/TableDataNoAvailable";
import PaginationControls from "./ui/PaginationControls";
import PerPageDropdown from "./ui/PerPageDropdown";
import Shimmer from "./ui/Shimmer"; // Import the Shimmer component
import { usePermissions } from "../context/PermissionContext";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import SortIcons from "./ui/SortIcons";
import ErrorAlert from "./ui/ErrorAlert";

const WorkFlowsSuggestionLayer = () => {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [modalType, setModalType] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [templateCode, setTemplateCode] = useState("");
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false); // New state for modal loading
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (roleName.trim()) {
      const formattedCode = roleName.trim().toLowerCase().replace(/\s+/g, "_");
      setTemplateCode(formattedCode);
    } else {
      setTemplateCode("");
    }
  }, [roleName]);

  const fetchSteps = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(
        "get",
        "/workflow/template/step-suggestions"
      );
      const apiData = response.data.data || [];
      const transformedData = apiData.map((item) => ({
        role_id: item.id,
        role_name: item.template_label,
        template_code: item.template_code,
        created_by: item.created_at
          ? new Date(item.created_at).toLocaleDateString()
          : "-",
        is_active: item.is_active,
      }));
      setRoles(transformedData);
      setFilteredRoles(transformedData);
      setTotalEntries(transformedData.length);
      setTotalPages(Math.ceil(transformedData.length / perPage));
    } catch (error) {
      console.error("API Fetch Error:", error);
      setErrorMessage("Failed to fetch templates. Please try again.");
      setRoles([]);
      setFilteredRoles([]);
      setTotalEntries(0);
      setTotalPages(1);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSteps();
  }, [perPage, saveLoading]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
    const filtered = roles.filter(
      (role) =>
        role.role_name?.toLowerCase().includes(value.toLowerCase()) ||
        role.template_code?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredRoles(filtered);
    setTotalEntries(filtered.length);
    setTotalPages(Math.ceil(filtered.length / perPage));
  };

  const handleSort = (field) => {
    setSortConfig((prev) => ({
      field,
      direction:
        prev.field === field && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedRoles = [...(filteredRoles || [])].sort((a, b) => {
    if (!sortConfig.field) return 0;
    const valueA = a[sortConfig.field] || "";
    const valueB = b[sortConfig.field] || "";
    if (sortConfig.field === "is_active") {
      const valA = valueA ? "Active" : "Inactive";
      const valB = valueB ? "Active" : "Inactive";
      return sortConfig.direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

  const handleOpenModal = (type, role = null) => {
    setModalType(type);
    setShowModal(true);
    setModalError("");
    setModalSuccess("");
    setRoleId(null);
    setRoleName("");
    setTemplateCode("");
    setSelectedRole(null);

    if (type === "edit" && role) {
      if (!role.role_id) {
        setModalError("Invalid or missing Template ID for this template.");
        return;
      }
      setModalLoading(true); // Start modal loading
      setTimeout(() => {
        setRoleId(role.role_id);
        setRoleName(role.role_name || "");
        setTemplateCode(role.template_code || "");
        setSelectedRole(role);
        setModalLoading(false); // Stop modal loading
      }, 1000); // Simulate 1-second API delay
    } else if (type === "delete" && role) {
      if (!role.role_id) {
        setModalError("Invalid or missing Template ID for this template.");
        return;
      }
      setSelectedRole(role);
      setRoleId(role.role_id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalType("");
    setRoleId(null);
    setRoleName("");
    setTemplateCode("");
    setModalError("");
    setModalSuccess("");
    setSelectedRole(null);
    setModalLoading(false); // Reset modal loading
  };

  const handleAddRole = async () => {
    if (!roleName.trim() || !templateCode.trim()) {
      setModalError("All fields are required.");
      return;
    }

    setSaveLoading(true);
    try {
      const newRole = {
        template_label: roleName,
        template_code: templateCode,
        default_order: roles.length + 1,
      };
      const response = await apiCall(
        "post",
        "/workflow/template/add-step",
        newRole
      );
      const addedRole = {
        role_id: response.data.data.id,
        role_name: response.data.template_label,
        template_code: response.data.template_code,
        created_by: response.data.created_at
          ? new Date(response.data.created_at).toLocaleDateString()
          : new Date().toLocaleDateString(),
        is_active: response.data.is_active || true,
      };
      const updatedRoles = [...roles, addedRole];
      setRoles(updatedRoles);
      setFilteredRoles(updatedRoles);
      setTotalEntries(updatedRoles.length);
      setTotalPages(Math.ceil(updatedRoles.length / perPage));
      setModalSuccess("Template added successfully!");
      setModalError("");
      setRoleName("");
      setTemplateCode("");
      handleCloseModal();
      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "POST");

      navigate(`/workflows-steps/${addedRole.role_id}`);
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "POST");
      setModalError("Failed to add template. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!roleName.trim() || !templateCode.trim()) {
      setModalError("All fields are required.");
      return;
    }

    if (!roleId) {
      setModalError("Invalid or missing Template ID.");
      return;
    }

    setSaveLoading(true);
    try {
      const updatedRole = {
        template_label: roleName,
        template_code: templateCode,
        default_order: selectedRole?.default_order || roles.length + 1,
      };

      const response = await apiCall(
        "put",
        `/workflow/template/update-step/${roleId}`,
        updatedRole,
        {
          Authorization: `Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL3BldmJhY2tlbmQuY29kb3Zpby5jb20vcHVibGljIiwic3ViIjozLCJpYXQiOjE3NTQwNTA5MzEsImV4cCI6MTc1NDEzNzMzMSwianRpIjoiNjg4Y2IxNzNlOGVkMyJ9.s8qgoObxnbVj3vsbWwlXQ5JlqV_znAXHK83WgLMFsZw`,
        }
      );

      const updatedRoles = roles.map((role) =>
        role.role_id === roleId
          ? {
              ...role,
              role_name: response.data.template_label || roleName,
              template_code: response.data.template_code || templateCode,
              default_order: response.data.default_order || role.default_order,
            }
          : role
      );

      setRoles(updatedRoles);
      setFilteredRoles(updatedRoles);
      setTotalEntries(updatedRoles.length);
      setTotalPages(Math.ceil(updatedRoles.length / perPage));
      setModalSuccess("Template updated successfully!");
      setModalError("");
      setRoleName("");
      setTemplateCode("");
      setRoleId(null);
      handleCloseModal();
      const handleRes = response.data;
      handleApiResponse(handleRes, "success", "PUT");
    } catch (error) {
      setModalError(
        error.response?.data?.message ||
          "Failed to update template. Please try again."
      );
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleId) {
      setModalError("Invalid or missing Template ID.");
      handleCustomResponse("error", "Invalid or missing Template ID.");
      return;
    }

    setSaveLoading(true);
    try {
      const res = await apiCall(
        "delete",
        `/workflow/template/delete-step/${roleId}`
      );
      const updatedRoles = roles.filter((role) => role.role_id !== roleId);
      setRoles(updatedRoles);
      setFilteredRoles(updatedRoles);
      setTotalEntries(updatedRoles.length);
      setTotalPages(Math.ceil(updatedRoles.length / perPage));
      setModalSuccess("Template deleted successfully!");
      setModalError("");
      handleCloseModal();
      const handleRes = res.data;
      handleApiResponse(handleRes, "success", "DELETE");
    } catch (error) {
      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "DELETE");
      setModalError("Failed to delete template. Please try again.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSubmit = () => {
    if (modalType === "add") {
      handleAddRole();
    } else if (modalType === "edit") {
      handleUpdateRole();
    } else if (modalType === "delete") {
      handleDeleteRole();
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (endPage - startPage < maxPagesToShow - 1) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const toggleExpand = (roleId, field) => {
    setExpandedRows((prev) => ({
      ...prev,
      [`${roleId}_${field}`]: !prev[`${roleId}_${field}`],
    }));
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const startEntry = totalEntries > 0 ? (currentPage - 1) * perPage + 1 : 0;
  const endEntry = Math.min(currentPage * perPage, totalEntries);

  if (error) {
    return <ErrorAlert />;
  }

  return (
    <div className="p-4 p-md-3 p-sm-2">
      <style>
        {`
          .table-responsive .table td, .table-responsive .table th {
            max-width: 200px;
            vertical-align: middle;
          }
          .show-more-btn {
            color: #E63C23;
            cursor: pointer;
            font-size: 12px;
            margin-left: 5px;
            background: none;
            border: none;
            padding: 0;
          }
          .show-more-btn:hover {
            text-decoration: underline;
          }
          @media (max-width: 576px) {
            .table-responsive .table td, .table-responsive .table th {
              max-width: 100px;
            }
            .show-more-btn {
              font-size: 10px;
            }
          }
        `}
      </style>

      <div className="p-4 p-md-3 p-sm-2">
        <div className="card shadow-sm">
          <div className="card p-3 p-sm-2 p-md-3">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div
                className="icon-field flex-grow-1"
                style={{ maxWidth: "400px" }}
              >
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search by template label or code..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <span className="icon">
                  <Icon icon="ion:search-outline" />
                </span>
              </div>
              <PerPageDropdown
                perPage={perPage}
                handlePerPageChange={(value) => {
                  setPerPage(value);
                  setCurrentPage(1);
                  setTotalPages(Math.ceil(filteredRoles.length / value));
                }}
              />
              <div className="ms-auto">
                {hasPermission("insert_workflow_suggestion_step") && (
                  <button
                    className="btn btn-primary-600 btn-sm d-flex align-items-center"
                    onClick={() => handleOpenModal("add")}
                  >
                    <Icon icon="ri:add-line" className="me-1" /> Add Template
                  </button>
                )}
              </div>
            </div>

            {errorMessage && (
              <div
                className="alert alert-danger alert-dismissible fade show mb-3"
                role="alert"
              >
                {errorMessage}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setErrorMessage("")}
                ></button>
              </div>
            )}

            {sortedRoles.length === 0 && !loading ? (
              <TableDataNoAvailable message="No templates found." />
            ) : (
              <div className="position-relative">
                <div
                  className="table-responsive"
                  style={{
                    opacity: loading ? 0.4 : 1,
                    pointerEvents: loading ? "none" : "auto",
                  }}
                >
                  <table className="table bordered-table mb-0 table-hover">
                    <thead>
                      <tr
                        style={{ backgroundColor: "#475EAB", color: "white" }}
                      >
                        <th scope="col" className="p-2">
                          <span>S.No</span>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort("role_name")}
                          className="p-2 d-lg-table-cell"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Template Label</span>
                            <SortIcons
                              field="assigned_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort("template_code")}
                          className="p-2 d-lg-table-cell"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Template Code</span>
                            <SortIcons
                              field="assigned_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort("created_by")}
                          className="p-2 d-lg-table-cell"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Created Date</span>
                            <SortIcons
                              field="assigned_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th
                          scope="col"
                          onClick={() => handleSort("is_active")}
                          className="p-2 d-lg-table-cell"
                          style={{ cursor: "pointer" }}
                        >
                          <div className="d-flex ">
                            <span>Status</span>
                            <SortIcons
                              field="assigned_at"
                              sortConfig={sortConfig}
                            />
                          </div>
                        </th>
                        <th scope="col" className="p-2">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRoles
                        .slice(
                          (currentPage - 1) * perPage,
                          currentPage * perPage
                        )
                        .map((role, index) => (
                          <tr key={role.role_id || index}>
                            <td className="p-2">
                              {(currentPage - 1) * perPage + index + 1}
                            </td>
                            <td className="p-2">
                              <span className="">
                                {expandedRows[`${role.role_id}_role_name`]
                                  ? role.role_name || "-"
                                  : truncateText(role.role_name || "-")}
                                {role.role_name &&
                                  role.role_name.length > 50 && (
                                    <button
                                      className="show-more-btn"
                                      onClick={() =>
                                        toggleExpand(role.role_id, "role_name")
                                      }
                                    >
                                      {expandedRows[`${role.role_id}_role_name`]
                                        ? "Show Less"
                                        : "Show More"}
                                    </button>
                                  )}
                              </span>
                            </td>
                            <td className="p-2">
                              <span className="">
                                {expandedRows[`${role.role_id}_template_code`]
                                  ? role.template_code || "-"
                                  : truncateText(role.template_code || "-")}
                                {role.template_code &&
                                  role.template_code.length > 50 && (
                                    <button
                                      className="show-more-btn"
                                      onClick={() =>
                                        toggleExpand(
                                          role.role_id,
                                          "template_code"
                                        )
                                      }
                                    >
                                      {expandedRows[
                                        `${role.role_id}_template_code`
                                      ]
                                        ? "Show Less"
                                        : "Show More"}
                                    </button>
                                  )}
                              </span>
                            </td>
                            <td className="p-2">{role.created_by || "-"}</td>
                            <td className="p-2">
                              <span
                                className={`px-3 py-1 radius-4 fw-medium text-sm ${
                                  role.is_active
                                    ? "bg-success-focus text-success-600 border border-success-main"
                                    : "bg-danger-focus text-danger-600 border border-danger-main"
                                }`}
                              >
                                {role.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="p-2">
                              {hasPermission(
                                "insert_workflow_suggestion_step"
                              ) && (
                                <button
                                  className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() =>
                                    navigate(`/workflows-steps/${role.role_id}`)
                                  }
                                  style={{
                                    position: "relative",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                    const tooltip =
                                      document.createElement("div");
                                    tooltip.textContent = "Go To Step";
                                    tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                    tooltip.id = "delete-tooltip";
                                    e.currentTarget.appendChild(tooltip);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                    const tooltip =
                                      document.getElementById("delete-tooltip");
                                    if (tooltip) tooltip.remove();
                                  }}
                                >
                                  <Icon icon="ic:outline-checklist" />
                                </button>
                              )}

                              {hasPermission(
                                "insert_workflow_suggestion_step"
                              ) && (
                                <button
                                  className="w-32-px h-32-px me-8 bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() => handleOpenModal("edit", role)}
                                  style={{
                                    position: "relative",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                    const tooltip =
                                      document.createElement("div");
                                    tooltip.textContent = "Edit";
                                    tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                    tooltip.id = "edit-tooltip";
                                    e.currentTarget.appendChild(tooltip);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                    const tooltip =
                                      document.getElementById("edit-tooltip");
                                    if (tooltip) tooltip.remove();
                                  }}
                                >
                                  <Icon icon="lucide:edit" />
                                </button>
                              )}
                              {hasPermission(
                                "delete_workflow_suggestion_step"
                              ) && (
                                <button
                                  className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                  onClick={() =>
                                    handleOpenModal("delete", role)
                                  }
                                  style={{
                                    position: "relative",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.1)";
                                    const tooltip =
                                      document.createElement("div");
                                    tooltip.textContent = "Delete";
                                    tooltip.style.cssText = `
                                  position: absolute;
                                  top: -35px;
                                  left: 50%;
                                  transform: translateX(-50%);
                                  background-color: #333;
                                  color: white;
                                  padding: 5px 8px;
                                  border-radius: 4px;
                                  font-size: 12px;
                                  white-space: nowrap;
                                  z-index: 1000;
                                  pointer-events: none;
                                `;
                                    tooltip.id = "delete-tooltip";
                                    e.currentTarget.appendChild(tooltip);
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1)";
                                    const tooltip =
                                      document.getElementById("delete-tooltip");
                                    if (tooltip) tooltip.remove();
                                  }}
                                >
                                  <Icon icon="mingcute:delete-2-line" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {loading && (
                  <div
                    className="position-absolute top-50 start-50 translate-middle d-flex justify-content-center align-items-center"
                    style={{
                      zIndex: 10,
                      borderRadius: "10px",
                      padding: "20px",
                    }}
                  >
                    <LoadingComponent />
                  </div>
                )}

                <PaginationControls
                  pagination={{
                    currentPage,
                    perPage,
                    total: totalEntries,
                  }}
                  handlePageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          backgroundColor: showModal ? "rgba(0,0,0,0.5)" : "transparent",
        }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {modalType === "add"
                  ? "Add New Template"
                  : modalType === "edit"
                  ? "Edit Template"
                  : "Confirm Delete Template"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
              ></button>
            </div>
            <div className="modal-body">
              {modalError && (
                <div className="alert alert-danger">{modalError}</div>
              )}
              {modalSuccess && (
                <div className="alert alert-success">{modalSuccess}</div>
              )}
              {modalType === "delete" ? (
                <p>
                  Are you sure you want to delete{" "}
                  <strong>{selectedRole?.role_name || "this template"}</strong>?
                </p>
              ) : modalType === "edit" && modalLoading ? (
                <Shimmer type="modal" /> // Show shimmer for edit modal while loading
              ) : (
                <>
                  <div className="mb-3">
                    <label htmlFor="roleName" className="form-label">
                      Template Label
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="roleName"
                      value={roleName}
                      onChange={(e) => setRoleName(e.target.value)}
                      placeholder="Enter template label"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="templateCode" className="form-label">
                      Template Code
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="templateCode"
                      value={templateCode}
                      readOnly
                      placeholder="Enter template code"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${
                  modalType === "delete" ? "btn-danger" : "btn-primary-600"
                }`}
                onClick={handleSubmit}
                disabled={saveLoading || modalLoading} // Disable button during modal loading
              >
                {modalType === "add"
                  ? saveLoading
                    ? "Please wait..."
                    : "Add Template"
                  : modalType === "edit"
                  ? saveLoading
                    ? "Please wait..."
                    : "Update Template"
                  : saveLoading
                  ? "Please wait..."
                  : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkFlowsSuggestionLayer;
